import { Component, ElementRef, ViewChild } from "@angular/core";
import {
  ActiveStatus,
  DocSubmitType,
  FeatureKind,
  Manufacturer,
  ManufacturerFeatureKindMap,
  ManufacturerFeedStatus,
  ManufacturerImage,
} from "app/model/entities/entity-model";
import { ManufacturerFeedStatusEnum } from "app/model/entities/enums";
import { FeatureKindEnum } from "app/model/entities/-feature-kind-enum";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";
import { StagedFeedApiService } from "@/admin/staged-feed-api.service";
import { AddressComponent } from "app/shared/address.component";
import { EntityFns } from "app/shared/entity-fns";
import { ImageHandlerComponent } from "app/shared/image/image-handler.component";
import { UtilFns } from "app/shared/util-fns";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { firstValueFrom } from "rxjs";

export interface EditManufacturerDialogParams extends DialogParams {
  // empty manufacturer means to add one.
  manufacturer?: Manufacturer;
}

@Component({
  templateUrl: "./edit-manufacturer-dialog.component.html",
  standalone: false,
})
export class EditManufacturerDialogComponent extends DialogBaseComponent<EditManufacturerDialogParams> {
  @ViewChild(ImageHandlerComponent) imageHandler?: ImageHandlerComponent;
  @ViewChild("firstInput") firstInput: ElementRef<HTMLInputElement>;
  @ViewChild("billingAddress") billingAddress: AddressComponent;
  @ViewChild("warehouseAddress") warehouseAddress: AddressComponent;

  manufacturer: Manufacturer;
  isAdding: boolean;

  activeStatuses: ActiveStatus[];
  docSubmitTypes: DocSubmitType[];
  manufacturerFeedStatuses: ManufacturerFeedStatus[];

  /** Exposed so the template can name the states rather than compare against 2 and 3. */
  ManufacturerFeedStatusEnum = ManufacturerFeedStatusEnum;

  featureKinds: FeatureKind[] = [];
  requiredKinds: ManufacturerFeatureKindMap[] = [];

  /**
   * Only a manufacturer with a feed has a shape worth stating. Nothing is reconciled against a
   * manufacturer without one, so a requirement there would be a rule nobody ever checks.
   */
  get isFeedEnabled() {
    const id = this.manufacturer?.manufacturerFeedStatusId;
    return id != null && id !== ManufacturerFeedStatusEnum.None;
  }

  /**
   * Other is the default a feature carries until somebody classifies it - "genuinely neither a
   * size nor a color". Requiring one would be meaningless, so it is never offered.
   */
  get selectableFeatureKinds() {
    return this.featureKinds.filter(k => k.id !== FeatureKindEnum.Other);
  }

  /**
   * Four chooser rows, each holding a feature kind or nothing. Four because that covers every
   * shape seen so far, not because anything refuses a fifth - loadKindSlots keeps whatever the
   * data already has, so a manufacturer configured past four stays editable.
   */
  static readonly KIND_SLOT_COUNT = 4;
  kindSlots: (number | null)[] = [];
  kindOptions: { id: number | null; name: string }[] = [];

  /**
   * Captured on open, not read live, so the chooser cannot vanish under the admin the moment
   * they clear the last slot - they still need to see the empty result they just made.
   */
  private hadRequirementsOnOpen = false;

  /**
   * A manufacturer with no feed and nothing configured has no shape worth stating, so the
   * chooser stays out of the way.
   *
   * It still appears when rows exist, because the server enforces them whether or not there is
   * a feed. Turning a feed off does not delete the requirements, and a hidden chooser would
   * leave a manufacturer whose every style save fails with no screen that can fix it.
   */
  get showKindChooser() {
    return this.isFeedEnabled || this.hadRequirementsOnOpen;
  }

  /** True only in that stranded case, which earns a sentence saying why the chooser is here. */
  get hasOrphanedRequirements() {
    return !this.isFeedEnabled && this.hadRequirementsOnOpen;
  }

  /**
   * The stranded case AND something still selected. Once every slot is cleared the rule really
   * is off, so the warning would sit under requiredShapeText contradicting it - one line saying
   * the system checks nothing, the next saying it still refuses a style.
   */
  get showOrphanWarning() {
    return this.hasOrphanedRequirements && this.hasAnyKindSelected;
  }

  get hasAnyKindSelected() {
    return this.kindSlots.some(k => k != null);
  }

  /**
   * Slots are the UI; rows are the storage. The primary key is (manufacturer, kind), so two
   * Size slots cannot be two rows - they are one row with requiredCount 2. This expands rows
   * into slots on open; collapseKindSlots folds them back on every change.
   */
  /** Slot position is the identity. The value is not: the same kind twice is the whole point. */
  trackBySlotIndex(index: number) {
    return index;
  }

  private loadKindSlots() {
    const slots: (number | null)[] = [];
    for (const r of this.requiredKinds) {
      if (r.entityAspect.entityState.isDeleted()) continue;
      for (let i = 0; i < Math.max(1, r.requiredCount ?? 1); i++) slots.push(r.featureKindId);
    }
    while (slots.length < EditManufacturerDialogComponent.KIND_SLOT_COUNT) slots.push(null);
    this.kindSlots = slots;
  }

  /**
   * Order within the slots carries no meaning - Size, Size, Color is the same requirement as
   * Color, Size, Size - so only the counts are stored.
   */
  collapseKindSlots() {
    const counts = new Map<number, number>();
    for (const kindId of this.kindSlots) {
      if (kindId == null) continue;
      counts.set(kindId, (counts.get(kindId) ?? 0) + 1);
    }

    for (const [kindId, count] of counts) {
      const existing = this.requiredKinds.find(r => r.featureKindId === kindId);
      if (existing == null) {
        this.requiredKinds.push(
          this.dbSaveService.createEntity(ManufacturerFeatureKindMap, {
            manufacturerId: this.manufacturer.id,
            featureKindId: kindId,
            requiredCount: count,
          }),
        );
        continue;
      }
      // Reject before setting the count, or rejectChanges puts the old count straight back.
      if (existing.entityAspect.entityState.isDeleted()) existing.entityAspect.rejectChanges();
      if (existing.requiredCount !== count) existing.requiredCount = count;
    }

    // No row means the kind is not required. Zero is never stored, so clearing the last slot of
    // a kind deletes its row rather than writing a count of nothing.
    for (const r of [...this.requiredKinds]) {
      if (counts.has(r.featureKindId)) continue;
      if (r.entityAspect.entityState.isDeleted()) continue;
      EntityFns.deleteOrDetach(r.entityAspect);
      if (r.entityAspect.entityState.isDetached()) {
        this.requiredKinds = this.requiredKinds.filter(x => x !== r);
      }
    }
  }

  /** Reads back the shape in words, so the admin can check it without counting rows. */
  get requiredShapeText() {
    const counts = new Map<number, number>();
    for (const kindId of this.kindSlots) {
      if (kindId == null) continue;
      counts.set(kindId, (counts.get(kindId) ?? 0) + 1);
    }
    if (counts.size === 0) return "Each style can carry any features. The system checks nothing.";

    const parts: string[] = [];
    for (const [kindId, count] of counts) {
      const name = this.featureKinds.find(k => k.id === kindId)?.name ?? "?";
      parts.push(count === 1 ? `one ${name} feature` : `${count} ${name} features`);
    }
    const list = parts.length === 1
      ? parts[0]
      : parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1];
    return `Each style must carry ${list}, and nothing else of those kinds.`;
  }

  constructor(
    public config: DynamicDialogConfig<EditManufacturerDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
    private stagedFeedApi: StagedFeedApiService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      width: "max(800px, 45vw)",
      height: "max(650px, 50vh)",
    });
    this.prepare();
  }

  /**
   * Refuses None the moment it is picked while the manufacturer still has feed styles. The server
   * refuses the save as well (ValidateFeedStatusOff); saying it here means the admin does not
   * finish the rest of the form first and lose it to a refusal at the end.
   */
  async onFeedStatusChange(next: ManufacturerFeedStatusEnum) {
    const previous = this.manufacturer.manufacturerFeedStatusId;
    this.manufacturer.manufacturerFeedStatusId = next;
    if (next !== ManufacturerFeedStatusEnum.None || this.isAdding) return;

    const summary = await this.stagedFeedApi.getFeedStyleSummary(this.manufacturer.id);
    if (summary.StyleCount === 0) return;

    this.manufacturer.manufacturerFeedStatusId = previous;
    await this.dialogService.showOkMessage(
      "Remove the Feed Styles First",
      `${this.manufacturer.name} has ${summary.StyleCount} feed style(s).`
        + ` You cannot set the feed status to None while they exist.`
        + ` Ask the system administrator to remove the feed styles.`
    );
  }

  private async prepare() {
    this.manufacturer = this.params.manufacturer;
    if (this.manufacturer == null) {
      this.isAdding = true;
      this.manufacturer = this.createManufacturerAsUnchanged();
    }

    this.config.header = this.isAdding ? "Add Manufacturer" : "Edit Manufacturer";

    this.activeStatuses = this.dbQueryService.getAllCached(ActiveStatus);
    this.docSubmitTypes = this.dbQueryService.getAllCached(DocSubmitType);
    this.manufacturerFeedStatuses = this.dbQueryService.getAllCached(ManufacturerFeedStatus);

    // getAlreadyFetched, not getAllCached: the latter throws when the cache is empty, which for
    // a lookup this new means a dialog nobody can get past. An empty list just means no kinds
    // are offered, and the server still refuses a style with the wrong shape either way.
    this.featureKinds = this.dbQueryService.getAlreadyFetched(FeatureKind);
    if (!this.isAdding) {
      this.requiredKinds = await this.dbQueryService.getRequiredFeatureKinds(this.manufacturer.id);
    }
    // Built once rather than from a getter: a dropdown fed a new array on every change
    // detection pass loses its selection while the list is open.
    this.kindOptions = [
      { id: null, name: "(none)" },
      ...this.selectableFeatureKinds.map(k => ({ id: k.id as number | null, name: k.name })),
    ];
    this.loadKindSlots();
    this.hadRequirementsOnOpen = this.requiredKinds.some(
      r => !r.entityAspect.entityState.isDeleted());

    this.isPageReady = true;

    // Focus the first input after the view renders
    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  private createManufacturerAsUnchanged() {
    const manufacturer = this.uow.createEntity(Manufacturer, {});
    manufacturer.entityAspect.setUnchanged();
    manufacturer.entityAspect.validateEntity();
    return manufacturer;
  }

  /** Create the join entity between Image and parent entity */
  createImageJoinEntity(event: { imageId: number; isPrimary: boolean }): void {
    this.dbSaveService.uow.createEntity(ManufacturerImage, {
      manufacturerId: this.manufacturer.id,
      imageId: event.imageId,
      isPrimary: event.isPrimary,
    });
  }

  async onTabChanged(e: any) {
    await UtilFns.wait(1);
    switch (e.index) {
      case 0:
        this.firstInput?.nativeElement?.focus();
        break;
      case 1:
        this.billingAddress?.focus();
        break;
      case 2:
        this.warehouseAddress?.focus();
        break;
    }
  }

  /** Returns true if the form has validation errors */
  get hasValidationErrors(): boolean {
    if (!this.manufacturer?.entityAspect) return false;
    this.manufacturer.entityAspect.validateEntity();
    return this.manufacturer.entityAspect.getValidationErrors().length > 0;
  }

  async ok() {
    const ok = await this.save();
    if (!ok) return;
    this.dialogRef.close(true);
  }

  async save() {
    // remove excess spaces

    if (this.isAdding) {
      this.manufacturer.entityAspect.setAdded();
    }

    const isValid = await this.validate();
    if (!isValid) {
      return false;
    }

    await this.imageHandler?.attachFiles();

    try {
      const sr = await this.dbSaveService.saveChanges();
      return true;
    } catch (e) {
      return false;
    }
  }

  async validate() {
    let matchingEntity = await this.dbQueryService.getMatchingManufacturer(this.manufacturer);
    if (matchingEntity != null) {
      await this.dialogService.showOkMessage(
        "Duplicate Name",
        `Cannot save because this manufacturer's name is a duplicate of an existing name`
      );
      return false;
    }

    // Mirrors ValidateFeedManufacturerHasShape. Zero kinds does not loosen the style check, it
    // turns it off, so a feed with nothing selected is a guard that reads as on and does
    // nothing. Caught here as well as on the server so the message names the field.
    if (this.isFeedEnabled && !this.kindSlots.some(k => k != null)) {
      await this.dialogService.showOkMessage(
        "Feature Kind Required",
        "This manufacturer has a feed, so it must need at least one feature kind."
          + " Select a feature kind, or set Manufacturer Feed to None.",
      );
      return false;
    }

    const ea = this.manufacturer.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
      await this.dialogService.showOkMessage("Validation Error(s)", errMsg);
      return false;
    }

    return true;
  }

  async cancel() {
    this.isPageReady = false;

    // The map rows are separate entities in the SHARED entity manager, not properties of the
    // manufacturer, so rejecting the manufacturer alone leaves every slot change pending.
    // save() commits the whole change set, so the next Ok on ANY manufacturer carries them -
    // which is how SanMar lost both of its kinds to a save on a different row.
    for (const r of this.requiredKinds) {
      EntityFns.rejectOrDetach(r.entityAspect);
    }
    this.requiredKinds = [];

    if (this.isAdding) {
      this.manufacturer.entityAspect.setDetached();
    } else {
      this.manufacturer.entityAspect?.rejectChanges();
    }

    this.dialogRef.close(false);
  }
}
